import { createFileRoute, redirect } from '@tanstack/react-router'
import InsightsDashboardPage from '@dashboard/InsightsDashboardPage'
import { DashboardQueries, isNotFound } from '@dashboard/data'
import {
  LegacyInsightsLinkNotice,
  useIsFromLegacyInsightsLink,
} from '@routes/_auth/insights/-legacyInsightsLink'

/**
 * A dashboard ID is a positive whole number of at most 15 digits, so exact as a JavaScript number.
 * Anything else names no dashboard; parsing does not fail on it, so that the loader can send it to
 * the same page as a dashboard the user may not see.
 */
const parseDashboardId = (raw: string) => {
  const id = /^\d{1,15}$/.test(raw) ? Number(raw) : 0
  return id > 0 ? id : undefined
}

const unknownDashboard = () =>
  redirect({ to: '/404', search: { reason: 'insightsDashboard' }, replace: true })

export const Route = createFileRoute('/_auth/insights/$dashboardId')({
  component: RouteComponent,
  params: {
    parse: ({ dashboardId }) => ({ dashboardId: parseDashboardId(dashboardId) }),
    stringify: ({ dashboardId }) => ({ dashboardId: String(dashboardId) }),
  },
  beforeLoad: ({ context: { scopedEnablements } }) => {
    if (!scopedEnablements.global().hasStandardEnablement('Analytics', 'READ')) {
      throw redirect({
        to: '/403',
        replace: true,
      })
    }
  },
  loader: async ({ context: { queryClient }, params: { dashboardId } }) => {
    if (dashboardId === undefined) {
      throw unknownDashboard()
    }
    try {
      return await queryClient.ensureQueryData(DashboardQueries.insightsDashboard(dashboardId))
    } catch (error) {
      // Missing, or none of the user's roles may see it: the endpoint does not say which.
      if (isNotFound(error)) {
        throw unknownDashboard()
      }
      throw error
    }
  },
})

function RouteComponent() {
  const dashboard = Route.useLoaderData()
  // TODO CRPM-4245 27R1: remove with the legacy Insights links; see TODO.md.
  const fromLegacyLink = useIsFromLegacyInsightsLink()

  return (
    <InsightsDashboardPage
      dashboard={dashboard}
      notice={fromLegacyLink && <LegacyInsightsLinkNotice />}
    />
  )
}
